import { useCallback, useContext, useState, useEffect } from 'react';
import { utcDay } from 'd3-time';

import { findString } from 'helpers/findString';
import { EntityContext } from 'context/entityContext';

/**
 * useSocialTopPostsData transform data from the social top posts endpoint to
 * data that is usable by a bubblechart, or any other chart component that
 * accepts an array of trends with structure [{entity,data}, ...].
 *
 * @param {array} socialTopPostsData socialTopPostsData PromiseState
 * @param {number} selectedPlatform The currently selected platform. -1 if no platform selected
 * @param {boolean} isOverall Is this overall mode?
 */
export function useSocialTopPostsData(
  socialTopPostsData,
  selectedPlatform,
  isOverall
) {
  const [trends, setTrends] = useState([]);

  const { entityIds } = useContext(EntityContext);

  const byEntityId = useCallback(
    (a, b) =>
      entityIds.findIndex((id) => id === a.entity.id) -
      entityIds.findIndex((id) => id === b.entity.id),
    [entityIds]
  );

  useEffect(() => {
    // If all prerequisites have been fulfilled
    if (socialTopPostsData.fulfilled) {
      let socialTopPosts = [];

      // and this is overall mode
      if (isOverall) {
        // we need to find the overall metric trend for each platform
        socialTopPosts = socialTopPostsData.value
          ?.map((entity) => ({
            ...entity,
            data: entity.data
              // filter by 'total' engagement
              .filter(byTotal)
              // remap trend data arrays to only contain the highest value for that day,
              // across all trend data arrays.
              .map(toOnlyHighestDailyValues)
              // Add Date objects to the trend data.
              .map(addDates)
              // Sort ascending by platform id.
              .sort(ascendingByPlatformId),
          }))
          .sort(byEntityId)
          // Flatten the trends to a normalized structure
          .flatMap(normalize);
      }

      // if this is not overall mode
      if (!isOverall) {
        // we need to find the platform specific overall metric trend
        socialTopPosts = socialTopPostsData.value
          .map((entity) => ({
            ...entity,
            data: entity.data
              // filter by selected platform id
              .filter(byPlatform(selectedPlatform))
              // filter by 'total' engagement
              .filter(byTotal)
              // Add Date objects to the trend data.
              .map(addDates),
          }))
          // Flatten the trends to a normalized structure
          .flatMap(normalize);
      }

      setTrends(socialTopPosts);
    }
  }, [byEntityId, isOverall, selectedPlatform, socialTopPostsData]);

  return trends;
}

/**
 * Find or filter a platform by platform id
 * @param {number} id
 * @returns {boolean}
 */
const byPlatform = (id) => (trend) => trend.platformId === id;

/**
 * Create a function that searches for 'total' in a string.
 * @returns {function}
 */
const hasTotal = findString('total');

/**
 * Does a metric name contain 'total'?
 * @param {object} metric
 * @returns {boolean}
 */
export const byTotal = (metric) => hasTotal(metric.metricName);

/**
 * Sort ascending by a value of a key in two similar objects
 * @param {string} key Used to get value from objects
 */
const ascendingBy = (key) => (a, b) => a[key] - b[key];

/**
 * Sort ascending by platformId
 * @returns {function}
 */
const ascendingByPlatformId = ascendingBy('platformId');

/**
 * Adds Date instances to a metrics' trend data objects.
 * Dates are floored (to AM midnight) to the same day in the UTC timezone.
 *
 * @param {object} metric
 * @returns {object} metric with dates added
 */
function addDates(metric) {
  return {
    ...metric,
    data: metric.data.map((d) => ({ ...d, date: utcDay.floor(d.periodKey) })),
  };
}

/**
 * Normalizes entity/platform/metric data to the same data structure that all types
 * of trend chart (Line/Bubble) use. [{entity: {}, data: []}]. In the case of
 * social top posts, the entity object also include metric and platform metadata.
 *
 * @param {object} entity
 * @returns {array} An array of each objects for each platform an entity supports.
 */
function normalize(entity) {
  return entity.data.map((platform) => {
    const { platformId, platformName, metricId, metricName, data } = platform;

    return {
      entity: entity.entity,
      platform: { id: platformId, name: platformName },
      metric: { id: metricId, name: metricName },
      data,
    };
  });
}

/**
 * Array.prototype.map() callback usable to null out all the points that are
 * not the highest of that day across all trends.
 *
 * @param {object} metric The current metric and its trend data
 * @param {number} idx The current element's array index
 * @param {array} arr The array this callback is called on.
 * @returns {object} Metric with possibly nullified trend data objects
 */
function toOnlyHighestDailyValues(metric, idx, arr) {
  // Clone the array without this metric to compare.
  const comparator = [...arr];
  comparator.splice(idx, 1);

  const data = [...metric.data].map((d) => {
    const byPeriodKey = (el) => el.periodKey === d.periodKey;

    // Are there metrics on the same day with a higher value?
    const comparatorHasHigherValues = comparator.some((comparatorMetric) => {
      const match = comparatorMetric.data.find(byPeriodKey);
      return match.value > d.value;
    });

    if (comparatorHasHigherValues) {
      return { ...d, value: null };
    }

    return d;
  });

  return { ...metric, data };
}
